var __defProp = Object.defineProperty;
var __typeError = (msg) => {
  throw TypeError(msg);
};
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
var __accessCheck = (obj, member, msg) => member.has(obj) || __typeError("Cannot " + msg);
var __privateGet = (obj, member, getter) => (__accessCheck(obj, member, "read from private field"), getter ? getter.call(obj) : member.get(obj));
var __privateAdd = (obj, member, value) => member.has(obj) ? __typeError("Cannot add the same private member more than once") : member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
var __privateSet = (obj, member, value, setter) => (__accessCheck(obj, member, "write to private field"), setter ? setter.call(obj, value) : member.set(obj, value), value);
var _queue, _scheduled, _initialized;
const requestIdleCallback = typeof window.requestIdleCallback == "function" ? window.requestIdleCallback : setTimeout;
const yieldToMainThread = () => {
  if ("yield" in scheduler) {
    return scheduler.yield();
  }
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      setTimeout(resolve, 0);
    });
  });
};
function isLowPowerDevice() {
  return Number(navigator.hardwareConcurrency) <= 2 || Number(navigator.deviceMemory) <= 2;
}
function supportsViewTransitions() {
  return typeof document.startViewTransition === "function";
}
const viewTransition = {
  current: void 0
};
const viewTransitionTypes = {
  "product-grid": async () => {
    const grid = document.querySelector(".product-grid");
    const productCards = (
      /** @type {HTMLElement[]} */
      [
        ...document.querySelectorAll(".product-grid .product-grid__item")
      ]
    );
    if (!grid || !productCards.length) return;
    await new Promise(
      (resolve) => requestIdleCallback(() => {
        const cardsToAnimate = getCardsToAnimate(grid, productCards);
        productCards.forEach((card, index) => {
          if (index < cardsToAnimate) {
            card.style.setProperty(
              "view-transition-name",
              `product-card-${card.dataset.productId}`
            );
          } else {
            card.style.setProperty("content-visibility", "hidden");
          }
        });
        resolve(null);
      })
    );
    return () => productCards.forEach((card) => {
      card.style.removeProperty("view-transition-name");
      card.style.removeProperty("content-visibility");
    });
  }
};
function startViewTransition(callback, types) {
  if (!supportsViewTransitions() || isLowPowerDevice() || prefersReducedMotion()) {
    callback();
    return Promise.resolve();
  }
  return new Promise(async (resolve) => {
    let cleanupFunctions = [];
    if (types) {
      for (const type of types) {
        if (viewTransitionTypes[type]) {
          const cleanupFunction = await viewTransitionTypes[type]();
          if (cleanupFunction) cleanupFunctions.push(cleanupFunction);
        }
      }
    }
    const transition = document.startViewTransition(callback);
    if (!viewTransition.current) {
      viewTransition.current = transition.finished;
    }
    if (types) types.forEach((type) => transition.types.add(type));
    transition.finished.then(() => {
      viewTransition.current = void 0;
      cleanupFunctions.forEach((cleanupFunction) => cleanupFunction());
      resolve();
    });
  });
}
function fetchConfig(type = "json", config = {}) {
  const headers = {
    "Content-Type": "application/json",
    Accept: `application/${type}`,
    ...config.headers
  };
  if (type === "javascript") {
    headers["X-Requested-With"] = "XMLHttpRequest";
    delete headers["Content-Type"];
  }
  return {
    method: "POST",
    headers: (
      /** @type {HeadersInit} */
      headers
    ),
    body: config.body
  };
}
function debounce(fn, wait) {
  let timeout;
  function debounced(...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn.apply(this, args), wait);
  }
  debounced.cancel = () => {
    clearTimeout(timeout);
  };
  return (
    /** @type {T & { cancel(): void }} */
    debounced
  );
}
function throttle(fn, delay) {
  let lastCall = 0;
  function throttled(...args) {
    const now = performance.now();
    if (now - lastCall >= delay) {
      lastCall = now;
      fn.apply(this, args);
    }
  }
  throttled.cancel = () => {
    lastCall = performance.now();
  };
  return (
    /** @type {T & { cancel(): void }} */
    throttled
  );
}
const reducedMotion = matchMedia("(prefers-reduced-motion: reduce)");
function prefersReducedMotion() {
  return reducedMotion.matches;
}
function normalizeString(str) {
  return str.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase();
}
function onDocumentLoaded(callback) {
  if (document.readyState === "complete") {
    callback();
  } else {
    window.addEventListener("load", callback);
  }
}
function onDocumentReady(callback) {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", callback);
  } else {
    callback();
  }
}
function removeWillChangeOnAnimationEnd(event) {
  const target = event.target;
  if (target && target instanceof HTMLElement) {
    target.style.setProperty("will-change", "unset");
    target.removeEventListener("animationend", removeWillChangeOnAnimationEnd);
  }
}
function onAnimationEnd(elements, callback, options = { subtree: true }) {
  const animations = Array.isArray(elements) ? elements.flatMap((element) => element.getAnimations(options)) : elements.getAnimations(options);
  const animationPromises = animations.reduce(
    (acc, animation) => {
      if (animation.timeline instanceof DocumentTimeline) {
        acc.push(animation.finished);
      }
      return acc;
    },
    /** @type {Promise<Animation>[]} */
    []
  );
  return Promise.allSettled(animationPromises).then(callback);
}
function isClickedOutside(event, element) {
  if (event.target instanceof HTMLDialogElement || !(event.target instanceof Element)) {
    return !isPointWithinElement(event.clientX, event.clientY, element);
  }
  return !element.contains(event.target);
}
function isPointWithinElement(x, y, element) {
  const { left, right, top, bottom } = element.getBoundingClientRect();
  return x >= left && x <= right && y >= top && y <= bottom;
}
const NAV_HOVER_CLIP_PROPERTY = {
  left: "--nav-hover-clip-left",
  right: "--nav-hover-clip-right"
};
function setNavHoverSlideEnter(item, event) {
  if (!(event instanceof PointerEvent)) return;
  const { left, width } = item.getBoundingClientRect();
  const enteredFromLeft = event.clientX < left + width / 2;
  const hidingProperty = enteredFromLeft ? NAV_HOVER_CLIP_PROPERTY.right : NAV_HOVER_CLIP_PROPERTY.left;
  const otherProperty = enteredFromLeft ? NAV_HOVER_CLIP_PROPERTY.left : NAV_HOVER_CLIP_PROPERTY.right;
  item.classList.add("menu-list__link--no-slide-transition");
  item.style.setProperty(hidingProperty, "100%");
  item.style.setProperty(otherProperty, "0%");
  void item.offsetWidth;
  item.classList.remove("menu-list__link--no-slide-transition");
  item.style.setProperty(hidingProperty, "0%");
}
function setNavHoverSlideExit(item, event) {
  if (!(event instanceof PointerEvent)) return;
  const { left, width } = item.getBoundingClientRect();
  const leavingToLeft = event.clientX < left + width / 2;
  const hidingProperty = leavingToLeft ? NAV_HOVER_CLIP_PROPERTY.right : NAV_HOVER_CLIP_PROPERTY.left;
  const otherProperty = leavingToLeft ? NAV_HOVER_CLIP_PROPERTY.left : NAV_HOVER_CLIP_PROPERTY.right;
  item.style.setProperty(otherProperty, "0%");
  item.style.setProperty(hidingProperty, "100%");
}
const mediaQueryLarge = matchMedia("(min-width: 750px)");
function isMobileBreakpoint() {
  return !mediaQueryLarge.matches;
}
function isDesktopBreakpoint() {
  return mediaQueryLarge.matches;
}
function isTouchDevice() {
  return "ontouchstart" in window && navigator.maxTouchPoints > 0;
}
function clamp(value, min, max) {
  return Math.max(min, Math.min(value, max));
}
function center(element, axis) {
  const { left, width, top, height } = element.getBoundingClientRect();
  const point = {
    x: left + width / 2,
    y: top + height / 2
  };
  if (axis) return (
    /**  @type {any} */
    point[axis]
  );
  return (
    /**  @type {any} */
    point
  );
}
function start(element, axis) {
  const { left, top } = element.getBoundingClientRect();
  const point = { x: left, y: top };
  if (axis) return (
    /**  @type {any} */
    point[axis]
  );
  return (
    /**  @type {any} */
    point
  );
}
function closest(values, target) {
  return values.reduce(function(prev, curr) {
    return Math.abs(curr - target) < Math.abs(prev - target) ? curr : prev;
  });
}
function preventDefault(event) {
  event.preventDefault();
}
function getVisibleElements(root, elements, ratio = 1, axis) {
  if (!elements?.length) return [];
  const rootRect = root.getBoundingClientRect();
  return elements.filter((element) => {
    const { width, height, top, right, left, bottom } = element.getBoundingClientRect();
    if (ratio < 1) {
      const intersectionLeft = Math.max(rootRect.left, left);
      const intersectionRight = Math.min(rootRect.right, right);
      const intersectionWidth = Math.max(
        0,
        intersectionRight - intersectionLeft
      );
      if (axis === "x") {
        return width > 0 && intersectionWidth / width >= ratio;
      }
      const intersectionTop = Math.max(rootRect.top, top);
      const intersectionBottom = Math.min(rootRect.bottom, bottom);
      const intersectionHeight = Math.max(
        0,
        intersectionBottom - intersectionTop
      );
      if (axis === "y") {
        return height > 0 && intersectionHeight / height >= ratio;
      }
      const intersectionArea = intersectionWidth * intersectionHeight;
      const elementArea = width * height;
      return elementArea > 0 && intersectionArea / elementArea >= ratio;
    }
    const isWithinX = left >= rootRect.left && right <= rootRect.right;
    if (axis === "x") {
      return isWithinX;
    }
    const isWithinY = top >= rootRect.top && bottom <= rootRect.bottom;
    if (axis === "y") {
      return isWithinY;
    }
    return isWithinX && isWithinY;
  });
}
function getIOSVersion() {
  const { userAgent } = navigator;
  const isIOS = /(iPhone|iPad)/i.test(userAgent);
  if (!isIOS) return null;
  const version = userAgent.match(/OS ([\d_]+)/)?.[1];
  const [major, minor] = version?.split("_") || [];
  if (!version || !major) return null;
  return {
    fullString: version.replace("_", "."),
    major: parseInt(major, 10),
    minor: minor ? parseInt(minor, 10) : 0
  };
}
function getCardsToAnimate(grid, cards) {
  if (!grid || !cards || cards.length === 0) return 0;
  const itemSample = cards[0];
  if (!itemSample) return 0;
  const gridRect = grid.getBoundingClientRect();
  const visibleArea = {
    top: Math.max(0, gridRect.top),
    bottom: Math.min(window.innerHeight, gridRect.bottom)
  };
  const visibleHeight = Math.round(visibleArea.bottom - visibleArea.top);
  if (visibleHeight <= 0) return 0;
  const cardSample = itemSample.querySelector("product-card");
  const gridStyle = getComputedStyle(grid);
  const galleryAspectRatio = cardSample?.refs?.cardGallery?.style.getPropertyValue(
    "--gallery-aspect-ratio"
  ) || "";
  let aspectRatio = parseFloat(galleryAspectRatio) || 0.5;
  if (galleryAspectRatio?.includes("/")) {
    const [width = "1", height = "2"] = galleryAspectRatio.split("/");
    aspectRatio = parseInt(width, 10) / parseInt(height, 10);
  }
  const cardGap = parseInt(
    cardSample?.refs?.productCardLink?.style.getPropertyValue(
      "--product-card-gap"
    ) || ""
  ) || 12;
  const gridGap = parseInt(gridStyle.getPropertyValue("--product-grid-gap")) || 12;
  const detailsSize = ((parseInt(gridStyle.fontSize) || 16) + 2) * 2;
  const isMobile = window.innerWidth < 750;
  const cardWidth = isMobile ? Math.round((gridRect.width - gridGap) / 2) : 100;
  const cardHeight = Math.round(cardWidth / aspectRatio) + cardGap + detailsSize;
  const columnsInGrid = isMobile ? 2 : Math.floor((gridRect.width + gridGap) / (cardWidth + gridGap));
  const rowsInGrid = Math.ceil(
    (visibleHeight - gridGap) / (cardHeight + gridGap)
  );
  return columnsInGrid * rowsInGrid;
}
function preloadImage(src) {
  const image = new Image();
  image.src = src;
}
class TextComponent extends HTMLElement {
  shimmer() {
    this.setAttribute("shimmer", "");
  }
}
if (!customElements.get("text-component")) {
  customElements.define("text-component", TextComponent);
}
function resetShimmer(container = document.body) {
  const shimmer = container.querySelectorAll("[shimmer]");
  shimmer.forEach((item) => item.removeAttribute("shimmer"));
}
function changeMetaThemeColor(color) {
  const metaThemeColor = document.head.querySelector(
    'meta[name="theme-color"]'
  );
  if (metaThemeColor && color) {
    metaThemeColor.setAttribute("content", color);
  }
}
function getViewParameterValue() {
  return new URLSearchParams(window.location.search).get("view");
}
function parseIntOrDefault(value, defaultValue) {
  if (value === null || value === void 0 || value === "") {
    return defaultValue;
  }
  const parsed = parseInt(value.toString());
  return isNaN(parsed) ? defaultValue : parsed;
}
class Scheduler {
  constructor() {
    /** @type {Set<() => void>} */
    __privateAdd(this, _queue, /* @__PURE__ */ new Set());
    /** @type {boolean} */
    __privateAdd(this, _scheduled, false);
    /** @param {() => void} task */
    __publicField(this, "schedule", async (task) => {
      __privateGet(this, _queue).add(task);
      if (!__privateGet(this, _scheduled)) {
        __privateSet(this, _scheduled, true);
        if (viewTransition.current) await viewTransition.current;
        requestAnimationFrame(this.flush);
      }
    });
    __publicField(this, "flush", () => {
      for (const task of __privateGet(this, _queue)) {
        setTimeout(task, 0);
      }
      __privateGet(this, _queue).clear();
      __privateSet(this, _scheduled, false);
    });
  }
}
_queue = new WeakMap();
_scheduled = new WeakMap();
const scheduler = new Scheduler();
function oncePerEditorSession(element, sessionKeyName, callback) {
  const isInThemeEditor = window.Shopify?.designMode;
  const shopifyEditorSectionId = JSON.parse(
    element.dataset.shopifyEditorSection || "{}"
  ).id;
  const shopifyEditorBlockId = JSON.parse(
    element.dataset.shopifyEditorBlock || "{}"
  ).id;
  const editorId = shopifyEditorSectionId || shopifyEditorBlockId;
  const uniqueSessionKey = `${sessionKeyName}-${editorId}`;
  if (isInThemeEditor && sessionStorage.getItem(uniqueSessionKey)) return;
  callback();
  if (isInThemeEditor) sessionStorage.setItem(uniqueSessionKey, "true");
  return;
}
class ResizeNotifier extends ResizeObserver {
  /**
   * @param {ResizeObserverCallback} callback
   */
  constructor(callback) {
    super((entries) => {
      if (__privateGet(this, _initialized)) return callback(entries, this);
      __privateSet(this, _initialized, true);
    });
    __privateAdd(this, _initialized, false);
  }
  disconnect() {
    __privateSet(this, _initialized, false);
    super.disconnect();
  }
}
_initialized = new WeakMap();
function setHeaderMenuStyle() {
  const headerComponent = (
    /** @type {HTMLElement} | null */
    document.querySelector("#header-component")
  );
  if (headerComponent) {
    window.requestAnimationFrame(() => {
      const overflowList = headerComponent?.querySelector("overflow-list");
      const hasReachedMinimum = overflowList && overflowList.hasAttribute("minimum-reached");
      headerComponent.dataset.menuStyle = window.innerWidth < 991 || hasReachedMinimum ? "drawer" : "menu";
    });
  }
}
function calculateHeaderGroupHeight(header = document.querySelector("#header-component"), headerGroup = document.querySelector("#header-group")) {
  if (!headerGroup) return 0;
  let totalHeight = 0;
  const children = headerGroup.children;
  for (let i = 0; i < children.length; i++) {
    const element = children[i];
    if (element === header || !(element instanceof HTMLElement)) continue;
    totalHeight += element.offsetHeight;
  }
  if (header instanceof HTMLElement && header.hasAttribute("transparent") && header.parentElement?.nextElementSibling) {
    return totalHeight + header.offsetHeight;
  }
  return totalHeight;
}
function updateTransparentHeaderOffset() {
  const header = document.querySelector("#header-component");
  const headerGroup = document.querySelector("#header-group");
  const hasHeaderSection = headerGroup?.querySelector(".header-section");
  if (!hasHeaderSection || !header?.hasAttribute("transparent")) {
    document.body.style.setProperty("--transparent-header-offset-boolean", "0");
    return;
  }
  const hasImmediateSection = hasHeaderSection.nextElementSibling?.classList.contains("shopify-section");
  const shouldApplyOffset = !hasImmediateSection ? "1" : "0";
  document.body.style.setProperty(
    "--transparent-header-offset-boolean",
    shouldApplyOffset
  );
}
function updateHeaderHeights() {
  const header = document.querySelector("header-component");
  if (!(header instanceof HTMLElement)) return;
  const headerHeight = header.offsetHeight;
  const headerGroupHeight = calculateHeaderGroupHeight(header);
  const headerTopRow = (
    /** @type {HTMLElement} | null */
    header.querySelector(".header__row--top")
  );
  document.body.style.setProperty("--header-height", `${headerHeight}px`);
  document.body.style.setProperty(
    "--header-group-height",
    `${headerGroupHeight}px`
  );
  if (headerTopRow) {
    window.requestAnimationFrame(function() {
      header.style.setProperty(
        "--top-row-height",
        `${headerTopRow.offsetHeight}px`
      );
    });
  }
}
function updateAllHeaderCustomProperties() {
  updateHeaderHeights();
  updateTransparentHeaderOffset();
  setHeaderMenuStyle();
}
if (typeof Theme !== "undefined") {
  Theme.utilities = {
    ...Theme.utilities,
    scheduler
  };
}
export {
  ResizeNotifier,
  TextComponent,
  calculateHeaderGroupHeight,
  center,
  changeMetaThemeColor,
  clamp,
  closest,
  debounce,
  fetchConfig,
  getIOSVersion,
  getViewParameterValue,
  getVisibleElements,
  isClickedOutside,
  isDesktopBreakpoint,
  isLowPowerDevice,
  isMobileBreakpoint,
  isPointWithinElement,
  isTouchDevice,
  mediaQueryLarge,
  normalizeString,
  onAnimationEnd,
  onDocumentLoaded,
  onDocumentReady,
  oncePerEditorSession,
  parseIntOrDefault,
  prefersReducedMotion,
  preloadImage,
  preventDefault,
  removeWillChangeOnAnimationEnd,
  requestIdleCallback,
  resetShimmer,
  scheduler,
  setHeaderMenuStyle,
  setNavHoverSlideEnter,
  setNavHoverSlideExit,
  start,
  startViewTransition,
  supportsViewTransitions,
  throttle,
  updateAllHeaderCustomProperties,
  viewTransition,
  yieldToMainThread
};
